import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getMemberLabData } from '../services/memberLabService';
import { getLabData } from '../services/labService';
import MainHeader from '../Components/MainHeader';

const ComparisonContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e1e5e9;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
`;

const Tab = styled.div`
  padding: 12px 5px;
  text-align: center;
  font-size: 13px;
  font-weight: bold;
  color: ${props => props.active ? 'white' : '#B0B0B0'};
  background-color: ${props => props.active ? '#00BBD3' : '#F2F2F2'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.2;
  white-space: nowrap;

  ${props => {
    if (props.children === 'สุขภาพของคุณ') {
      return `width: 152px;`;
    } else if (props.children === 'ผลตรวจรวม') {
      return `width: 120px;`;
    } else if (props.children === 'ผลตรวจเปรียบเทียบ') {
      return `width: 168px;`;
    }
    return '';
  }}

  &:hover {
    background-color: ${props => props.active ? '#00BBD3' : '#e9ecef'};
  }
`;

const Content = styled.div`
  padding: 24px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
`;

const ComparisonTable = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const TableContent = styled.div`
  min-width: fit-content;
`;

const TableHeader = styled.div`
  background-color: #00BBD3;
  color: white;
`;

const HeaderRow = styled.div`
  display: grid;
`;

const HeaderCell = styled.div`
  padding: 8px 4px;
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  word-break: break-word;
  white-space: nowrap;
  overflow: hidden;

  &:first-child {
    text-align: left;
    justify-content: flex-start;
    padding-left: 12px;
    white-space: normal;
  }

  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.div`
  background-color: white;
`;

const CategoryRow = styled.div`
  display: grid;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const CategoryCell = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: bold;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  text-align: left;
  grid-column: 1 / -1;
`;

const TableRow = styled.div`
  display: grid;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 8px 4px;
  font-size: 13px;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:last-child {
    border-right: none;
  }
`;

const TestNameCell = styled(TableCell)`
  justify-content: flex-start;
  text-align: left;
  padding-left: ${props => props.indent ? '24px' : '12px'};
  padding-right: 8px;
  color: #333333;
  font-size: 12px;
  background-color: #ffffff;
`;


const ComparisonResultsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testData, setTestData] = useState([]);
  const [dates, setDates] = useState([]);
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // ดึงข้อมูลจาก MemberLabList API (เส้นเดียวกับ admin)
        const memberLabData = await getMemberLabData();

        // ดึงข้อมูลจาก DailyHealthRecord (local backend, uploader='m')
        let localData = [];
        try {
          const localResponse = await getLabData('', 1);
          localData = localResponse?.data || localResponse?.result || localResponse?.list || [];
        } catch (e) {
          console.warn('⚠️ Could not load local lab data:', e);
        }

        if (memberLabData?.labData) {
          const comparisonData = prepareComparisonData(memberLabData, localData);
          setTestData(comparisonData.testData);
          setDates(comparisonData.dates);
        }

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const prepareComparisonData = (memberLabData, localData = []) => {
    if (!memberLabData?.labData) {
      return { testData: [], dates: [] };
    }

    const labData = memberLabData.labData;

    // visitDates จาก getMemberLabData() เรียงใหม่สุดก่อน → reverse เพื่อให้เก่าสุดก่อน
    const visitDates = [...(memberLabData.visitDates || [])].reverse();

    // Mapping ชื่อ field จาก DailyHealthRecord → normalized key ที่ใช้ใน getValues()
    const localFieldMap = {
      // Vital Signs
      'sys': 'systolic', 'dia': 'diastolic',
      'systolic': 'systolic', 'diastolic': 'diastolic',
      'pulse': 'pulse', 'weight': 'weight',
      'height': 'height', 'bmi': 'bmi',
      // Blood Sugar
      'sugar_fasting': 'fbs', 'fbs': 'fbs', 'fpg': 'fbs',
      'sugar_non_npo': 'non_npo_glucose',
      'hba1c': 'hba1c',
      // Kidney
      'creatinine': 'creatinine',
      'bun': 'bun',
      'egfr': 'egfr',
      // CBC
      'wbc': 'wbc',
      'rbc': 'rbc',
      'hb': 'hemoglobin',
      'hct': 'hematocrit',
      'plt': 'platelet',
      'mcv': 'mcv',
      'mch': 'mch',
      'mchc': 'mchc',
      'eosinophil': 'eosinophil',
      'eosinophile': 'eosinophil',
      'cbceosinophileresult': 'eosinophil',
      'eosinophil_percent': 'eosinophil',
      'eosinophile_percent': 'eosinophil',
      'eosinophil-count': 'eosinophil',
      'eosinophil_count': 'eosinophil',
      // Lipid (labService บันทึกเป็น 'chol', hospital API ก็ใช้ key 'chol')
      'chol': 'chol',
      'hdl': 'hdl',
      'ldl': 'ldl',
      'triglycerideresult': 'tg',
      // Liver
      'sgot': 'sgot',
      'sgpt': 'sgpt',
      'alp': 'alp',
      'ggt': 'ggt',
      'bilirubin': 'total_bilirubin',
      'albumin': 'albumin',
      'protein': 'total_protein',
      // Uric Acid
      'uric': 'uric',
      // Urine (ชื่อที่ HospitalCheckupForm บันทึก)
      'urinalysiscolorresul': 'urinalysis_color',
      'urinalysisapperancer': 'urinalysis_appearance',
      'urinalysisphresult': 'urinalysis_ph',
      'urinalysisspecificgr': 'urinalysis_specific_gravity',
      'urinalysisproteinres': 'urinalysis_protein',
      'urinalysissugarresul': 'urinalysis_glucose',
      'urinalysisketoneresu': 'urinalysis_ketone',
      'urinalysisbloodresul': 'urinalysis_blood',
      'urinalysisleukocyter': 'urinalysis_leukocyte',
      'urinalysisnitritere': 'urinalysis_nitrite',
      'urinalysisbilirubinr': 'urinalysis_bilirubin',
      'urinalysisurobilino': 'urinalysis_urobilinogen',
      'urinalysisrbcresult': 'urinalysis_rbc',
      'urinalysiswbcresult': 'urinalysis_wbc',
      'urinalysisbactcellre': 'bacteria',
      'urinalysisepithelial': 'epithelial',
      // Stool (HospitalCheckupForm ใช้ camelCase → toLowerCase ก่อน lookup)
      'stoolcolor': 'stool_color',
      'stoolconsistency': 'stool_consistency',
      'stoolrbc': 'stool_rbc',
      'stoolwbc': 'stool_wbc',
      'stoolparasite': 'stool_parasite',
      'stooloccultblood': 'stool_occult_blood',
    };

    // Merge ข้อมูล DailyHealthRecord (uploader='m') เข้า labData
    const existingDateParts = new Set(visitDates.map(d => d.split('T')[0].split(' ')[0]));

    localData
      .filter(item => item.uploader === 'm' && item.name && item.value && item.dateadd)
      .forEach(item => {
        const normalizedKey = localFieldMap[item.name.toLowerCase()];
        if (!normalizedKey) return;

        const datePart = item.dateadd.split('T')[0].split(' ')[0];

        // เพิ่ม entry ใน labData ถ้ายังไม่มีค่าจาก hospital API ในวันนั้น
        if (!labData[normalizedKey]) labData[normalizedKey] = [];
        const alreadyHasHospitalValue = labData[normalizedKey].some(e =>
          e.date.split('T')[0].split(' ')[0] === datePart
        );
        if (!alreadyHasHospitalValue) {
          labData[normalizedKey].push({ value: String(item.value), date: item.dateadd });
        }

        // เพิ่มวันที่ใหม่ใน visitDates ถ้ายังไม่มี
        if (!existingDateParts.has(datePart)) {
          visitDates.push(item.dateadd);
          existingDateParts.add(datePart);
        }
      });

    // เรียงวันที่ใหม่ (เก่าสุดก่อน) หลัง merge
    visitDates.sort((a, b) => new Date(a) - new Date(b));

    if (visitDates.length === 0) {
      return { testData: [], dates: [] };
    }

    // ตัด duplicate ที่วันเดียวกัน (เก็บแค่อันแรกของแต่ละวัน)
    const seenDateParts = new Set();
    const uniqueVisitDates = visitDates.filter(d => {
      const datePart = d.split('T')[0].split(' ')[0];
      if (seenDateParts.has(datePart)) return false;
      seenDateParts.add(datePart);
      return true;
    });

    const dates = uniqueVisitDates.map(d => formatThaiDate(d));

    // Helper function: หาค่าของแต่ละวันจาก labData (ที่ผ่าน normalizeLabName แล้ว)
    const getValues = (...fieldNames) => {
      return uniqueVisitDates.map(dateKey => {
        // normalize dateKey เป็น date-only (เพราะ visitDates อาจมี full datetime)
        const dateKeyPart = dateKey.split('T')[0].split(' ')[0];
        for (const fieldName of fieldNames) {
          const paramData = labData[fieldName];
          if (paramData && Array.isArray(paramData)) {
            const entry = paramData.find(e => {
              const entryDate = (e.date || '').split('T')[0].split(' ')[0];
              return entryDate === dateKeyPart;
            });
            if (entry && entry.value && entry.value !== '-') {
              return entry.value;
            }
          }
        }
        return '-';
      });
    };

    // Build comprehensive test data with ALL parameters
    const testData = [];

    // รายการตรวจ (Vital Signs)
    testData.push({ type: 'category', name: 'รายการตรวจ' });
    testData.push({ name: 'ความดันโลหิต (Systolic)', values: getValues('systolic'), unit: 'มม.ปรอท', indent: true });
    testData.push({ name: 'ความดันโลหิต (Diastolic)', values: getValues('diastolic'), unit: 'มม.ปรอท', indent: true });
    testData.push({ name: 'ชีพจร', values: getValues('pulse'), unit: 'ครั้ง/นาที', indent: true });
    testData.push({ name: 'อัตราการหายใจ', values: getValues('respiratory_rate', 'respiratoryrate'), unit: 'ครั้ง/นาที', indent: true });
    testData.push({ name: 'น้ำหนัก', values: getValues('weight'), unit: 'กก.', indent: true });
    testData.push({ name: 'ส่วนสูง', values: getValues('height'), unit: 'ซม.', indent: true });
    testData.push({ name: 'BMI', values: getValues('bmi'), unit: 'กก./ม.²', indent: true });

    // ความสมบูรณ์ของเลือด (CBC)
    testData.push({ type: 'category', name: 'ความสมบูรณ์ของเลือด' });
    testData.push({ name: 'เม็ดเลือดขาว (WBC)', values: getValues('wbc'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง (RBC)', values: getValues('rbc'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'ฮีโมโกลบิน (Hb)', values: getValues('hemoglobin', 'hb'), unit: 'g/dL', indent: true });
    testData.push({ name: 'ฮีมาโตคริต (Hct)', values: getValues('hematocrit', 'hct'), unit: '%', indent: true });
    testData.push({ name: 'เกล็ดเลือด (Platelet)', values: getValues('platelet', 'plt'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'MCV', values: getValues('mcv'), unit: 'fL', indent: true });
    testData.push({ name: 'MCH', values: getValues('mch'), unit: 'pg/cell', indent: true });
    testData.push({ name: 'MCHC', values: getValues('mchc'), unit: 'g/dL', indent: true });
    testData.push({ name: 'นิวโทรฟิล (Neutrophil)', values: getValues('neutrophile'), unit: '%', indent: true });
    testData.push({ name: 'ลิมโฟไซต์ (Lymphocyte)', values: getValues('lymphocyte-count', 'lymphocyte'), unit: '%', indent: true });
    testData.push({ name: 'โมโนไซต์ (Monocyte)', values: getValues('monocyte-count', 'monocyte'), unit: '%', indent: true });
    testData.push({ name: 'เบโซฟิล (Basophil)', values: getValues('basophil-count', 'basophil'), unit: '%', indent: true });
    testData.push({ name: 'อีโอซิโนฟิล (Eosinophil)', values: getValues('eosinophil', 'eosinophile', 'cbceosinophileresult', 'eosinophil_percent', 'eosinophile_percent', 'eosinophil-count', 'eosinophil_count'), unit: '%', indent: true });
    testData.push({ name: 'RBC morphology', values: getValues('rbc_morphology'), unit: '', indent: true });

    // น้ำตาลในเลือด
    testData.push({ type: 'category', name: 'น้ำตาลในเลือด' });
    testData.push({ name: 'น้ำตาลในเลือด (FBS)', values: getValues('sugar', 'fbs'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'น้ำตาลในเลือด (Non-NPO)', values: getValues('non_npo_glucose'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'HbA1C', values: getValues('hba1c'), unit: '%', indent: true });

    // ไขมันในเลือด
    testData.push({ type: 'category', name: 'ไขมันในเลือด' });
    testData.push({ name: 'คอเลสเตอรอลรวม', values: getValues('cholesterol', 'chol'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'HDL (ไขมันดี)', values: getValues('hdl'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'LDL (ไขมันไม่ดี)', values: getValues('ldl'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'ไตรกลีเซอไรด์', values: getValues('triglyceride', 'tg'), unit: 'mg/dL', indent: true });

    // การทำงานของไต
    testData.push({ type: 'category', name: 'การทำงานของไต' });
    testData.push({ name: 'Creatinine', values: getValues('creatinine'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'BUN', values: getValues('bun'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'eGFR', values: getValues('egfr'), unit: 'mL/min/1.73m²', indent: true });

    // การทำงานของตับ
    testData.push({ type: 'category', name: 'การทำงานของตับ' });
    testData.push({ name: 'SGOT (AST)', values: getValues('sgot', 'ast'), unit: 'U/L', indent: true });
    testData.push({ name: 'SGPT (ALT)', values: getValues('sgpt', 'alt'), unit: 'U/L', indent: true });
    testData.push({ name: 'ALP', values: getValues('alp'), unit: 'U/L', indent: true });
    testData.push({ name: 'GGT', values: getValues('ggt'), unit: 'U/L', indent: true });
    testData.push({ name: 'Total Bilirubin', values: getValues('total_bilirubin'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'Direct Bilirubin', values: getValues('direct_bilirubin'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'Albumin', values: getValues('albumin'), unit: 'g/dL', indent: true });
    testData.push({ name: 'Total Protein', values: getValues('total_protein'), unit: 'g/dL', indent: true });
    testData.push({ name: 'Globulin', values: getValues('globulin'), unit: 'g/dL', indent: true });

    // กรดยูริคในเลือด
    testData.push({ type: 'category', name: 'กรดยูริคในเลือด' });
    testData.push({ name: 'Uric Acid', values: getValues('uric_acid', 'uric'), unit: 'mg/dL', indent: true });

    // ผลการตรวจปัสสาวะ
    testData.push({ type: 'category', name: 'ผลการตรวจปัสสาวะ' });
    testData.push({ name: 'สีของปัสสาวะ', values: getValues('urinalysis_color'), unit: '', indent: true });
    testData.push({ name: 'ลักษณะปัสสาวะ', values: getValues('urinalysis_appearance'), unit: '', indent: true });
    testData.push({ name: 'pH', values: getValues('urinalysis_ph'), unit: '', indent: true });
    testData.push({ name: 'ความถ่วงจำเพาะ', values: getValues('urinalysis_specific_gravity'), unit: '', indent: true });
    testData.push({ name: 'โปรตีน', values: getValues('urinalysis_protein'), unit: '', indent: true });
    testData.push({ name: 'น้ำตาล', values: getValues('urinalysis_glucose'), unit: '', indent: true });
    testData.push({ name: 'คีโตน', values: getValues('urinalysis_ketone'), unit: '', indent: true });
    testData.push({ name: 'เลือด', values: getValues('urinalysis_blood'), unit: '', indent: true });
    testData.push({ name: 'Bilirubin', values: getValues('urinalysis_bilirubin'), unit: '', indent: true });
    testData.push({ name: 'Urobilinogen', values: getValues('urinalysis_urobilinogen'), unit: '', indent: true });
    testData.push({ name: 'Leukocyte', values: getValues('urinalysis_leukocyte'), unit: '', indent: true });
    testData.push({ name: 'Nitrite', values: getValues('urinalysis_nitrite'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง', values: getValues('urinalysis_rbc'), unit: 'cells/HPF', indent: true });
    testData.push({ name: 'เม็ดเลือดขาว', values: getValues('urinalysis_wbc'), unit: 'cells/HPF', indent: true });
    testData.push({ name: 'แบคทีเรีย', values: getValues('bacteria'), unit: '', indent: true });
    testData.push({ name: 'เซลล์เยื่อผิว', values: getValues('epithelial'), unit: '', indent: true });

    // ผลการตรวจอุจจาระ
    testData.push({ type: 'category', name: 'ผลการตรวจอุจจาระ' });
    testData.push({ name: 'สีของอุจจาระ', values: getValues('stool_color'), unit: '', indent: true });
    testData.push({ name: 'ลักษณะอุจจาระ', values: getValues('stool_consistency'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง', values: getValues('stool_rbc'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดขาว', values: getValues('stool_wbc'), unit: '', indent: true });
    testData.push({ name: 'พยาธิ, ไข่พยาธิ', values: getValues('stool_parasite'), unit: '', indent: true });

    // การตรวจเลือดแฝงในอุจจาระ
    testData.push({ type: 'category', name: 'การตรวจเลือดแฝงในอุจจาระ' });
    testData.push({ name: 'Hemoglobin Detection Stool', values: getValues('stool_occult_blood'), unit: '', indent: true });

    // เกลือแร่ในเลือด
    testData.push({ type: 'category', name: 'เกลือแร่ในเลือด' });
    testData.push({ name: 'Sodium', values: getValues('sodium'), unit: 'mmol/L', indent: true });
    testData.push({ name: 'Potassium', values: getValues('potassium'), unit: 'mmol/L', indent: true });
    testData.push({ name: 'Chloride', values: getValues('chloride'), unit: 'mmol/L', indent: true });
    testData.push({ name: 'Bicarbonate', values: getValues('bicarbonate'), unit: 'mmol/L', indent: true });
    testData.push({ name: 'Total Calcium', values: getValues('total_calcium', 'calcium'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'Inorganic Phosphate', values: getValues('inorganic_phosphate', 'phosphate'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'Magnesium', values: getValues('magnesium'), unit: 'mg/dL', indent: true });

    // ไวรัสตับอักเสบ
    testData.push({ type: 'category', name: 'ไวรัสตับอักเสบ' });
    testData.push({ name: 'HBsAg', values: getValues('hbsag'), unit: '', indent: true });
    testData.push({ name: 'HBsAg Concentration', values: getValues('hbsag_concentration'), unit: 'IU/mL', indent: true });
    testData.push({ name: 'HBsAb', values: getValues('hbsab'), unit: '', indent: true });
    testData.push({ name: 'Anti-HBs Concentration', values: getValues('anti_hbs', 'antihbs'), unit: 'IU/L', indent: true });
    testData.push({ name: 'HCV Ab', values: getValues('hcv_ab', 'hcvab'), unit: '', indent: true });
    testData.push({ name: 'COI', values: getValues('coi'), unit: '', indent: true });

    // ค่าบ่งชี้มะเร็ง
    testData.push({ type: 'category', name: 'ค่าบ่งชี้มะเร็ง' });
    testData.push({ name: 'AFP', values: getValues('afp'), unit: 'ng/ml', indent: true });
    testData.push({ name: 'PSA', values: getValues('psa'), unit: 'ng/ml', indent: true });
    testData.push({ name: 'CEA', values: getValues('cea'), unit: 'ng/mL', indent: true });
    testData.push({ name: 'CA19-9', values: getValues('ca19_9', 'ca19-9'), unit: 'U/mL', indent: true });
    testData.push({ name: 'CA125', values: getValues('ca125'), unit: 'U/mL', indent: true });

    // ฮอร์โมนไทรอยด์
    testData.push({ type: 'category', name: 'ฮอร์โมนไทรอยด์' });
    testData.push({ name: 'TSH', values: getValues('tsh'), unit: 'uIU/mL', indent: true });
    testData.push({ name: 'Free T3', values: getValues('free_t3', 'ft3'), unit: 'pg/mL', indent: true });
    testData.push({ name: 'Free T4', values: getValues('free_t4', 'ft4'), unit: 'ng/dL', indent: true });

    // การสัมผัสสารพิษกลุ่มยาฆ่าแมลง
    testData.push({ type: 'category', name: 'การสัมผัสสารพิษกลุ่มยาฆ่าแมลง' });
    testData.push({ name: 'Cholinesterase', values: getValues('cholinesterase'), unit: 'U/L', indent: true });

    return { testData, dates };
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '-';

    // แยกเฉพาะส่วนวันที่ ถ้ามี timestamp
    const datePart = dateString.split('T')[0].split(' ')[0];

    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return dateString; // ถ้า format ไม่ถูกต้องให้ return กลับไป

    const thaiYear = parseInt(year) + 543;
    const shortYear = thaiYear.toString().slice(-2);
    return `${day}/${month}/${shortYear}`;
  };

  const handleTabClick = (tab) => {
    if (tab === 'สุขภาพของคุณ') {
      window.location.href = '/dashboard';
    } else if (tab === 'ผลตรวจรวม') {
      window.location.href = '/test-results';
    }
  };

  // Calculate grid columns based on number of dates
  const gridColumns = `140px repeat(${dates.length}, 75px)`;

  console.log('📅 Total dates:', dates.length);
  console.log('📅 Dates array:', dates);
  console.log('📏 Grid columns:', gridColumns);

  return (
    <ComparisonContainer>
      <MainHeader />

      <TabContainer>
        <Tab onClick={() => handleTabClick('สุขภาพของคุณ')}>สุขภาพของคุณ</Tab>
        <Tab onClick={() => handleTabClick('ผลตรวจรวม')}>ผลตรวจรวม</Tab>
        <Tab active>ผลตรวจเปรียบเทียบ</Tab>
      </TabContainer>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : testData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลสำหรับเปรียบเทียบ
          </div>
        ) : (
          <ComparisonTable>
            <TableWrapper>
              <TableContent>
                <TableHeader>
                  <HeaderRow style={{ gridTemplateColumns: gridColumns }}>
                    <HeaderCell>รายการตรวจ</HeaderCell>
                    {dates.map((date, index) => (
                      <HeaderCell key={index}>{date}</HeaderCell>
                    ))}
                  </HeaderRow>
                </TableHeader>

                <TableBody>
                  {testData.map((item, index) =>
                    item.type === 'category' ? (
                      <CategoryRow key={index} style={{ gridTemplateColumns: gridColumns }}>
                        <CategoryCell>{item.name}</CategoryCell>
                      </CategoryRow>
                    ) : (
                      <TableRow key={index} style={{ gridTemplateColumns: gridColumns }}>
                        <TestNameCell indent={item.indent}>
                          {item.name}
                        </TestNameCell>
                        {item.values.map((value, vIndex) => (
                          <TableCell key={vIndex}>
                            {value} {value !== '-' && item.unit}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </TableContent>
            </TableWrapper>
          </ComparisonTable>
        )}
      </Content>
    </ComparisonContainer>
  );
};

export default ComparisonResultsPage;
